import { Card, Tabs, Select, Table, DatePicker, message, Tag, Row, Col, Statistic } from 'antd'
import { TeamOutlined, DollarOutlined, CalendarOutlined, IdcardOutlined } from '@ant-design/icons'
import { useState, useEffect } from 'react'
import axios from 'axios'
import dayjs from 'dayjs'

const DIMENSIONS = [
  { value: 'department', label: 'القسم' },
  { value: 'nationality', label: 'الجنسية' },
  { value: 'branch', label: 'الفرع' },
  { value: 'costCenter', label: 'مركز التكلفة' },
  { value: 'jobType', label: 'طبيعة الوظيفة' },
]

const EXPIRY_WINDOWS = [
  { value: 1, label: 'يوم واحد' },
  { value: 7, label: 'أسبوع' },
  { value: 15, label: '15 يوماً' },
  { value: 30, label: 'شهر' },
  { value: 90, label: '3 أشهر' },
]

function HRDashboard() {
  const token = localStorage.getItem('token')
  const headers = { Authorization: `Bearer ${token}` }

  // قسم 1: عدد الموظفين
  const [empCountDim, setEmpCountDim] = useState('department')
  const [empCountRows, setEmpCountRows] = useState([])
  const [empCountLoading, setEmpCountLoading] = useState(false)

  // قسم 2: إجمالي الرواتب
  const [salaryDim, setSalaryDim] = useState('department')
  const [salaryMonth, setSalaryMonth] = useState(dayjs())
  const [salaryRows, setSalaryRows] = useState([])
  const [salaryLoading, setSalaryLoading] = useState(false)

  // قسم 3: رصيد الإجازات
  const [vacationRows, setVacationRows] = useState([])
  const [vacationLoading, setVacationLoading] = useState(false)

  // قسم 4: البطاقات المنتهية
  const [expiryDays, setExpiryDays] = useState(30)
  const [expiryRows, setExpiryRows] = useState([])
  const [expiryLoading, setExpiryLoading] = useState(false)

  const fetchEmpCounts = async (dim = empCountDim) => {
    setEmpCountLoading(true)
    try {
      const res = await axios.get('/api/settings/hr/employee-counts', { headers, params: { groupBy: dim } })
      setEmpCountRows(res.data)
    } catch (err) {
      message.error(err.response?.data?.error || 'خطأ في جلب عدد الموظفين')
    }
    setEmpCountLoading(false)
  }

  const fetchSalaryTotals = async (dim = salaryDim, m = salaryMonth) => {
    setSalaryLoading(true)
    try {
      const res = await axios.get('/api/settings/hr/salary-totals', {
        headers,
        params: { groupBy: dim, month: m.month() + 1, year: m.year() }
      })
      setSalaryRows(res.data)
    } catch (err) {
      message.error(err.response?.data?.error || 'خطأ في جلب إجمالي الرواتب')
    }
    setSalaryLoading(false)
  }

  const fetchVacationBalances = async () => {
    setVacationLoading(true)
    try {
      const res = await axios.get('/api/settings/hr/vacation-balances', { headers })
      setVacationRows(res.data)
    } catch (err) {
      message.error(err.response?.data?.error || 'خطأ في جلب أرصدة الإجازات')
    }
    setVacationLoading(false)
  }

  const fetchExpiringCards = async (d = expiryDays) => {
    setExpiryLoading(true)
    try {
      const res = await axios.get('/api/settings/hr/expiring-cards', { headers, params: { days: d } })
      setExpiryRows(res.data)
    } catch (err) {
      message.error(err.response?.data?.error || 'خطأ في جلب البطاقات المنتهية')
    }
    setExpiryLoading(false)
  }

  useEffect(() => {
    fetchEmpCounts()
    fetchSalaryTotals()
    fetchExpiringCards()
    // رصيد الإجازات قد يكون بطيئاً (حلقة على 500+ موظف) — لا نجلبه
    // تلقائياً عند فتح الشاشة، بل عند فتح تبويبه تحديداً فقط
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const totalEmpCount = empCountRows.reduce((s, r) => s + r.count, 0)
  const totalSalary = salaryRows.reduce((s, r) => s + (r.totalSalary || 0), 0)

  const empCountColumns = [
    { title: DIMENSIONS.find(d => d.value === empCountDim)?.label, dataIndex: 'label', key: 'label' },
    { title: 'عدد الموظفين', dataIndex: 'count', key: 'count', align: 'left',
      render: v => <strong>{v.toLocaleString()}</strong> },
  ]

  const salaryColumns = [
    { title: DIMENSIONS.find(d => d.value === salaryDim)?.label, dataIndex: 'label', key: 'label' },
    { title: 'عدد الموظفين', dataIndex: 'empCount', key: 'empCount', align: 'left' },
    { title: 'إجمالي الراتب', dataIndex: 'totalSalary', key: 'totalSalary', align: 'left',
      render: v => <strong style={{ color: '#0f8a45' }}>{(v || 0).toLocaleString()} ر.س</strong> },
  ]

  const vacationColumns = [
    { title: 'كود الموظف', dataIndex: 'empCode', key: 'empCode', width: 110 },
    { title: 'اسم الموظف', dataIndex: 'empName', key: 'empName' },
    { title: 'الرصيد المستحق (يوم)', dataIndex: 'balance', key: 'balance', align: 'left', width: 180,
      render: v => <strong style={{ color: v < 0 ? '#ff4d4f' : '#0f8a45' }}>{v ?? '-'}</strong> },
  ]

  const expiryColumns = [
    { title: 'كود الموظف', dataIndex: 'EmpCode', key: 'EmpCode', width: 110 },
    { title: 'اسم الموظف', dataIndex: 'EmpName', key: 'EmpName' },
    { title: 'نوع البطاقة', dataIndex: 'CardTypeName', key: 'CardTypeName',
      render: v => <Tag color="blue">{v}</Tag> },
    { title: 'تاريخ الانتهاء', dataIndex: 'EndDate', key: 'EndDate', width: 130,
      render: v => dayjs(v).format('YYYY-MM-DD') },
  ]

  const items = [
    {
      key: 'counts',
      label: <span><TeamOutlined /> عدد الموظفين</span>,
      children: (
        <Card
          extra={
            <Select
              value={empCountDim}
              style={{ width: 180 }}
              options={DIMENSIONS}
              onChange={(v) => { setEmpCountDim(v); fetchEmpCounts(v) }}
            />
          }
        >
          <Statistic title="إجمالي الموظفين النشطين" value={totalEmpCount} style={{ marginBottom: 16 }} />
          <Table columns={empCountColumns} dataSource={empCountRows} rowKey="label" loading={empCountLoading} pagination={false} size="small" />
        </Card>
      )
    },
    {
      key: 'salaries',
      label: <span><DollarOutlined /> إجمالي الرواتب</span>,
      children: (
        <Card
          extra={
            <Row gutter={8}>
              <Col>
                <DatePicker
                  picker="month"
                  value={salaryMonth}
                  onChange={(m) => { setSalaryMonth(m); fetchSalaryTotals(salaryDim, m) }}
                />
              </Col>
              <Col>
                <Select
                  value={salaryDim}
                  style={{ width: 180 }}
                  options={DIMENSIONS}
                  onChange={(v) => { setSalaryDim(v); fetchSalaryTotals(v, salaryMonth) }}
                />
              </Col>
            </Row>
          }
        >
          <Statistic title="إجمالي الرواتب للشهر المختار" value={totalSalary} suffix="ر.س" precision={2}
            valueStyle={{ color: '#0f8a45' }} style={{ marginBottom: 16 }} />
          <Table columns={salaryColumns} dataSource={salaryRows} rowKey="label" loading={salaryLoading} pagination={false} size="small" />
        </Card>
      )
    },
    {
      key: 'vacation',
      label: <span><CalendarOutlined /> رصيد الإجازات</span>,
      children: (
        <Card>
          <p style={{ color: '#888', marginBottom: 16 }}>
            قد يستغرق جلب هذا التقرير بضع ثوانٍ (يُحسَب لكل موظف نشط على حدة).
          </p>
          <Table
            columns={vacationColumns}
            dataSource={vacationRows}
            rowKey="empCode"
            loading={vacationLoading}
            pagination={{ pageSize: 20 }}
            size="small"
            locale={{
              emptyText: vacationLoading ? 'جاري الحساب...' : (
                <span onClick={fetchVacationBalances} style={{ cursor: 'pointer', color: '#1c599e' }}>
                  اضغط هنا لجلب أرصدة الإجازات
                </span>
              )
            }}
          />
        </Card>
      )
    },
    {
      key: 'expiring',
      label: <span><IdcardOutlined /> البطاقات المنتهية</span>,
      children: (
        <Card
          extra={
            <Select
              value={expiryDays}
              style={{ width: 150 }}
              options={EXPIRY_WINDOWS}
              onChange={(v) => { setExpiryDays(v); fetchExpiringCards(v) }}
            />
          }
        >
          <Statistic title="عدد البطاقات المنتهية بالفترة المختارة" value={expiryRows.length} valueStyle={{ color: expiryRows.length > 0 ? '#ff4d4f' : undefined }} style={{ marginBottom: 16 }} />
          <Table columns={expiryColumns} dataSource={expiryRows} rowKey={(r, i) => i} loading={expiryLoading} pagination={{ pageSize: 20 }} size="small" />
        </Card>
      )
    },
  ]

  return (
    <div>
      <h2 style={{ marginBottom: 16 }}>شؤون الموظفين</h2>
      <Tabs items={items} />
    </div>
  )
}

export default HRDashboard
